import { type FC, useEffect, useRef, useState } from 'react';
import { useHostContext } from '@/context/HostContext.tsx';
import type { CreatorChatFlowMessage } from '@/types/creator-chat-flow';
import { cn } from '@/utils';

const CreatorChatFlowChat: FC<{
	isLoading?: boolean;
	isSendingMessage?: boolean;
	messages: CreatorChatFlowMessage[];
	onSendMessage?: (message: string) => Promise<void>;
}> = ({
	isLoading = false,
	isSendingMessage = false,
	messages,
	onSendMessage
}) => {
	const { localization } = useHostContext();
	const [message, setMessage] = useState('');
	const messagesContainerRef = useRef<HTMLDivElement | null>(null);
	const latestMessageId = messages.at(-1)?.id ?? null;
	const isSendDisabled = !message.trim() || !onSendMessage || isSendingMessage;

	useEffect(() => {
		const messagesContainer = messagesContainerRef.current;
		if (!messagesContainer || (!latestMessageId && !isLoading)) {
			return;
		}

		if (typeof messagesContainer.scrollTo === 'function') {
			messagesContainer.scrollTo({
				top: messagesContainer.scrollHeight,
				behavior: 'smooth'
			});
			return;
		}

		messagesContainer.scrollTop = messagesContainer.scrollHeight;
	}, [isLoading, latestMessageId]);

	const handleSendMessage = async () => {
		if (isSendDisabled) {
			return;
		}

		const nextMessage = message.trim();
		await onSendMessage(nextMessage);
		setMessage('');
	};

	return (
		<div
			className="flex h-[min(35vh,432px)] w-[min(38.5vw,544px)] min-w-[448px] flex-col overflow-hidden rounded-2xl border border-[#E8E8E8] bg-[rgba(244,247,250,0.53)] backdrop-blur-[20px]"
			data-testid="creator-chat-flow-chat"
		>
			<div
				className="flex-1 overflow-y-auto px-6 pb-5 pt-6"
				ref={messagesContainerRef}
			>
				<div className="flex min-h-full flex-col justify-end gap-3">
					{messages.length === 0
						? isLoading && (
								<div className="rounded-2xl bg-white/80 px-5 py-4 text-[15px] leading-[1.4] text-primary-900/55 shadow-[0_10px_28px_rgba(15,23,42,0.04)]">
									{localization.translate(
										'creator',
										'chatFlow.chat.loadingConversation'
									)}
								</div>
							)
						: messages.map((chatMessage) => {
								const isUser = chatMessage.role === 'user';

								return (
									<div
										className={cn('flex', {
											'justify-end': isUser,
											'justify-start': !isUser
										})}
										key={chatMessage.id}
									>
										<div
											className={cn(
												'max-w-[80%] rounded-2xl px-5 py-3 text-[16px] leading-[1.35] shadow-[0_10px_28px_rgba(15,23,42,0.04)]',
												{
													'bg-primary-900 text-white': isUser,
													'bg-white/90 text-primary-900': !isUser
												}
											)}
											data-role={chatMessage.role}
											data-testid={`creator-chat-flow-message-${chatMessage.id}`}
										>
											{chatMessage.text}
										</div>
									</div>
								);
							})}
				</div>
			</div>

			<div className="px-6 pb-6">
				<form
					className="flex items-center gap-3 rounded-2xl border border-primary-900/18 bg-white px-4 py-3 shadow-[0_10px_24px_rgba(15,23,42,0.04)]"
					onSubmit={(event) => {
						event.preventDefault();
						void handleSendMessage();
					}}
				>
					<input
						aria-label={localization.translate(
							'creator',
							'chatFlow.chat.messageInputAriaLabel'
						)}
						className="h-7 flex-1 border-0 bg-transparent text-[16px] leading-none text-primary-900/70 outline-none placeholder:text-primary-900/45"
						data-testid="creator-chat-flow-message-input"
						disabled={isSendingMessage}
						onChange={(event) => {
							setMessage(event.currentTarget.value);
						}}
						placeholder={localization.translate(
							'creator',
							'chatFlow.chat.messageInputPlaceholder'
						)}
						type="text"
						value={message}
					/>
					<button
						aria-label={localization.translate(
							'creator',
							'chatFlow.chat.sendMessageAriaLabel'
						)}
						className={cn(
							'flex h-9 w-9 items-center justify-center rounded-full border border-primary-900/18 text-primary-900 transition-colors duration-150',
							{
								'hover:bg-primary-900/[0.04]': !isSendDisabled,
								'cursor-not-allowed opacity-40': isSendDisabled
							}
						)}
						data-testid="creator-chat-flow-send-message-button"
						disabled={isSendDisabled}
						type="submit"
					>
						<svg
							aria-hidden="true"
							className="h-5 w-5"
							fill="none"
							stroke="currentColor"
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth="1.8"
							viewBox="0 0 24 24"
						>
							<path d="M4 20L20 12L4 4L7.6 10.7L14 12L7.6 13.3L4 20Z" />
						</svg>
					</button>
				</form>
			</div>
		</div>
	);
};

export default CreatorChatFlowChat;
